import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { notificationId, type NotificationId } from './Notification.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.notification_recipient_ */
export type NotificationRecipientId = number & {
  __brand: 'NotificationRecipientId'
}

/** Represents the table public.notification_recipient_ */
export default interface NotificationRecipientTable {
  /** Database type: pg_catalog.int4 */
  id: ColumnType<
    NotificationRecipientId,
    NotificationRecipientId | undefined,
    NotificationRecipientId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.uuid */
  notificationId: ColumnType<NotificationId, NotificationId, NotificationId>

  /** Database type: pg_catalog.uuid */
  userId: ColumnType<UserId, UserId, UserId>
}

export type NotificationRecipient = Selectable<NotificationRecipientTable>

export type NotificationRecipientInitializer =
  Insertable<NotificationRecipientTable>

export type NotificationRecipientMutator =
  Updateable<NotificationRecipientTable>

export const notificationRecipientId =
  z.number() as unknown as z.Schema<NotificationRecipientId>

export const notificationRecipient = z.object({
  id: notificationRecipientId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  notificationId: notificationId,
  userId: userId,
}) as unknown as z.Schema<NotificationRecipient>

export const notificationRecipientInitializer = z.object({
  id: notificationRecipientId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  notificationId: notificationId,
  userId: userId,
}) as unknown as z.Schema<NotificationRecipientInitializer>

export const notificationRecipientMutator = z.object({
  id: notificationRecipientId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  notificationId: notificationId.optional(),
  userId: userId.optional(),
}) as unknown as z.Schema<NotificationRecipientMutator>
